import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, User, Briefcase, Building2 } from "lucide-react";
import api, { formatCPF, formatCNPJ, formatPhone } from "@/lib/api";
import { toast } from "sonner";
import { getLandingUrl } from "@/lib/landingUrl";

const BR_STATES = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const TYPES = [
  { key: "clt", label: "Colaborador (CLT)", desc: "Pessoa Física com vínculo empregatício", icon: User, color: "blue" },
  { key: "pj", label: "Prestador de Serviço (PJ)", desc: "Pessoa Jurídica prestando serviço", icon: Briefcase, color: "purple" },
  { key: "company", label: "Empresa Pagadora", desc: "Empresa que paga colaboradores e prestadores", icon: Building2, color: "emerald" },
];

export default function RegisterPage() {
  const [step, setStep] = useState("type");
  const [regType, setRegType] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", password: "", cpf: "", cnpj: "",
    company_name: "", company_cnpj: "", company_phone: "",
    company_address_street: "", company_address_number: "", company_address_neighborhood: "",
    company_address_complement: "", company_address_zipcode: "", company_address_city: "", company_address_uf: "",
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const fetchCep = async (cep) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) return;
      setForm((prev) => ({
        ...prev,
        company_address_street: data.logradouro || prev.company_address_street,
        company_address_neighborhood: data.bairro || prev.company_address_neighborhood,
        company_address_complement: data.complemento || prev.company_address_complement,
        company_address_city: data.localidade || prev.company_address_city,
        company_address_uf: data.uf || prev.company_address_uf,
      }));
      toast.success("Endereço preenchido pelo CEP");
    } catch { /* silent */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.name.trim()) missing.push("Nome");
    if (!form.email.trim()) missing.push("Email");
    if (regType === "company") {
      if (!form.password || form.password.length < 8) missing.push("Senha (mín. 8 caracteres)");
      else if (!/[A-Z]/.test(form.password) || !/[0-9]/.test(form.password) || !/[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(form.password))
        missing.push("Senha (precisa maiúscula, número e especial)");
    } else {
      if (!form.password || form.password.length < 6) missing.push("Senha (mín. 6 caracteres)");
    }
    if (regType === "clt" && !form.cpf.trim()) missing.push("CPF");
    if (regType === "pj" && !form.cnpj.trim()) missing.push("CNPJ");
    if (regType === "company") {
      if (!form.company_name.trim()) missing.push("Nome da empresa");
      if (!form.company_cnpj.trim()) missing.push("CNPJ da empresa");
      if (!form.company_phone.trim()) missing.push("Telefone da empresa");
      if (!form.company_address_street.trim()) missing.push("Endereço");
      if (!form.company_address_number.trim()) missing.push("Número");
      if (!form.company_address_neighborhood.trim()) missing.push("Bairro");
      if (!form.company_address_zipcode.trim()) missing.push("CEP");
      if (!form.company_address_city.trim()) missing.push("Cidade");
      if (!form.company_address_uf) missing.push("Estado");
    }
    if (missing.length) { toast.error(`Campos obrigatórios: ${missing.join(", ")}`); return; }

    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, password: form.password,
        role: regType === "company" ? "company_admin" : "employee",
        register_type: regType,
        cpf: regType === "clt" ? form.cpf : undefined,
        cnpj: regType === "pj" ? form.cnpj : undefined,
        company_name: regType === "company" ? form.company_name : undefined,
        company_cnpj: regType === "company" ? form.company_cnpj : undefined,
        company_phone: regType === "company" ? form.company_phone : undefined,
        company_address_street: form.company_address_street || undefined,
        company_address_number: form.company_address_number || undefined,
        company_address_neighborhood: form.company_address_neighborhood || undefined,
        company_address_complement: form.company_address_complement || undefined,
        company_address_zipcode: form.company_address_zipcode || undefined,
        company_address_city: form.company_address_city || undefined,
        company_address_uf: form.company_address_uf || undefined,
      };
      const res = await api.post("/auth/register", payload);
      if (res.data.pending_approval) {
        toast.success(res.data.message);
        navigate("/login");
      } else {
        localStorage.setItem("token", res.data.token);
        toast.success("Conta criada com sucesso!");
        navigate(res.data.user.role === "employee" ? "/employee" : "/company/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar conta");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="/images/register-bg.jpg"
          alt="bg" className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <a
            href={getLandingUrl()}
            className="flex items-center gap-3 mb-6 group transition-opacity hover:opacity-80"
            data-testid="register-logo-landing-link"
            aria-label="Ir para a página inicial Kredz"
          >
            <img src="/logo-kredz-dark.png" alt="Kredz" className="h-14 w-auto transition-transform group-hover:scale-105" />
          </a>
          <h1 className="text-4xl font-bold mb-3 tracking-tight">Crie sua conta</h1>
          <p className="text-lg text-white/70 max-w-md">Comece a antecipar seus recebíveis de forma rápida e segura.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md space-y-6 animate-fade-in">
          <a
            href={getLandingUrl()}
            className="lg:hidden flex items-center gap-3 mb-2 group transition-opacity hover:opacity-80"
            data-testid="register-logo-landing-link-mobile"
            aria-label="Ir para a página inicial Kredz"
          >
            <img src="/logo-kredz-dark.png" alt="Kredz" className="h-12 w-auto transition-transform group-hover:scale-105" />
          </a>

          {step === "type" ? (
            <>
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Criar conta</h2>
                <p className="text-muted-foreground mt-2">Selecione o tipo de cadastro</p>
              </div>
              <div className="space-y-3">
                {TYPES.map((t) => (
                  <button key={t.key} data-testid={`register-type-${t.key}`}
                    onClick={() => { setRegType(t.key); setStep("form"); }}
                    className={`w-full p-4 rounded-xl border border-white/10 bg-black/20 hover:bg-${t.color}-500/10 hover:border-${t.color}-500/30 transition-all text-left flex items-center gap-4 group`}>
                    <div className={`p-2.5 rounded-lg bg-${t.color}-500/10 group-hover:bg-${t.color}-500/20 transition-colors`}>
                      <t.icon className={`w-5 h-5 text-${t.color}-400`} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t.label}</p>
                      <p className="text-xs text-muted-foreground">{t.desc}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => setStep("type")} className="shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">
                    {regType === "clt" ? "Colaborador (CLT)" : regType === "pj" ? "Prestador (PJ)" : "Empresa Pagadora"}
                  </h2>
                  <p className="text-sm text-muted-foreground">Preencha os dados para criar sua conta</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Personal info */}
                <div className="space-y-2">
                  <Label>Nome completo <span className="text-red-400">*</span></Label>
                  <Input data-testid="register-name-input" placeholder="Seu nome" value={form.name}
                    onChange={(e) => upd("name", e.target.value)} required
                    className="h-11 bg-black/20 border-white/10" />
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-red-400">*</span></Label>
                  <Input data-testid="register-email-input" type="email" placeholder="seu@email.com" value={form.email}
                    onChange={(e) => upd("email", e.target.value)} required
                    className="h-11 bg-black/20 border-white/10" />
                  {regType === "company" && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Se você já possui cadastro como colaborador, use o mesmo e-mail e <strong>a senha que já utiliza</strong> para
                      adicionar uma conta de Empresa Pagadora ao seu perfil existente.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Senha <span className="text-red-400">*</span></Label>
                  <Input data-testid="register-password-input" type="password"
                    placeholder={regType === "company" ? "Mín 8 chars, maiúscula, número, especial" : "Mínimo 6 caracteres"}
                    value={form.password}
                    onChange={(e) => upd("password", e.target.value)} required minLength={regType === "company" ? 8 : 6}
                    className="h-11 bg-black/20 border-white/10" />
                  {regType === "company" && form.password && (
                    <div className="text-xs space-y-0.5 mt-1">
                      <p className={form.password.length >= 8 ? "text-emerald-400" : "text-muted-foreground"}>{form.password.length >= 8 ? "✓" : "○"} Mínimo 8 caracteres</p>
                      <p className={/[A-Z]/.test(form.password) ? "text-emerald-400" : "text-muted-foreground"}>{/[A-Z]/.test(form.password) ? "✓" : "○"} Letra maiúscula</p>
                      <p className={/[0-9]/.test(form.password) ? "text-emerald-400" : "text-muted-foreground"}>{/[0-9]/.test(form.password) ? "✓" : "○"} Número</p>
                      <p className={/[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(form.password) ? "text-emerald-400" : "text-muted-foreground"}>{/[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(form.password) ? "✓" : "○"} Caractere especial</p>
                    </div>
                  )}
                </div>

                {regType === "clt" && (
                  <div className="space-y-2 animate-fade-in">
                    <Label>CPF <span className="text-red-400">*</span></Label>
                    <Input data-testid="register-cpf-input" placeholder="000.000.000-00" value={form.cpf}
                      onChange={(e) => upd("cpf", formatCPF(e.target.value.replace(/\D/g, "").slice(0, 11)))}
                      maxLength={14} className="h-11 bg-black/20 border-white/10" />
                  </div>
                )}
                {regType === "pj" && (
                  <div className="space-y-2 animate-fade-in">
                    <Label>CNPJ <span className="text-red-400">*</span></Label>
                    <Input data-testid="register-cnpj-input" placeholder="00.000.000/0000-00" value={form.cnpj}
                      onChange={(e) => upd("cnpj", formatCNPJ(e.target.value.replace(/\D/g, "").slice(0, 14)))}
                      maxLength={18} className="h-11 bg-black/20 border-white/10" />
                  </div>
                )}

                {regType === "company" && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="pt-2 border-t border-white/5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Dados da Empresa</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Razão Social <span className="text-red-400">*</span></Label>
                      <Input data-testid="register-company-name" placeholder="Nome da empresa" value={form.company_name}
                        onChange={(e) => upd("company_name", e.target.value)}
                        className="h-10 bg-black/20 border-white/10" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>CNPJ <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-cnpj" placeholder="00.000.000/0000-00" value={form.company_cnpj}
                          onChange={(e) => upd("company_cnpj", formatCNPJ(e.target.value.replace(/\D/g, "").slice(0, 14)))}
                          maxLength={18} className="h-10 bg-black/20 border-white/10" />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefone <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-phone" placeholder="(11) 99999-9999" value={form.company_phone}
                          onChange={(e) => upd("company_phone", formatPhone(e.target.value))}
                          maxLength={15} className="h-10 bg-black/20 border-white/10" />
                      </div>
                    </div>
                    <div className="pt-2 border-t border-white/5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Endereço da Empresa</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>CEP <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-cep" placeholder="00000-000" value={form.company_address_zipcode}
                          onChange={(e) => {
                            const d = e.target.value.replace(/\D/g, "").slice(0,8);
                            const formatted = d.replace(/(\d{5})(\d)/, "$1-$2");
                            upd("company_address_zipcode", formatted);
                            if (d.length === 8) fetchCep(formatted);
                          }}
                          maxLength={9} className="h-10 bg-black/20 border-white/10" />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Rua <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-street" value={form.company_address_street}
                          onChange={(e) => upd("company_address_street", e.target.value)}
                          className="h-10 bg-black/20 border-white/10" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>Número <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-number" value={form.company_address_number}
                          onChange={(e) => upd("company_address_number", e.target.value)}
                          className="h-10 bg-black/20 border-white/10" />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Complemento</Label>
                        <Input data-testid="register-company-complement" value={form.company_address_complement}
                          onChange={(e) => upd("company_address_complement", e.target.value)}
                          className="h-10 bg-black/20 border-white/10" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Bairro <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-neighborhood" value={form.company_address_neighborhood}
                          onChange={(e) => upd("company_address_neighborhood", e.target.value)}
                          className="h-10 bg-black/20 border-white/10" />
                      </div>
                      <div className="space-y-2">
                        <Label>Cidade <span className="text-red-400">*</span></Label>
                        <Input data-testid="register-company-city" value={form.company_address_city}
                          onChange={(e) => upd("company_address_city", e.target.value)}
                          className="h-10 bg-black/20 border-white/10" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Estado <span className="text-red-400">*</span></Label>
                      <select data-testid="register-company-uf" value={form.company_address_uf}
                        onChange={(e) => upd("company_address_uf", e.target.value)}
                        className="w-full h-10 rounded-md bg-black/20 border border-white/10 text-sm px-3 text-foreground">
                        <option value="">Selecione o estado</option>
                        {BR_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                <Button data-testid="register-submit-btn" type="submit" disabled={loading}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  {loading ? <div className="loading-spinner" /> : <>Criar conta <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </form>
            </>
          )}

          <div className="text-center text-sm text-muted-foreground">
            Já tem conta?{" "}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="login-link">
              Fazer login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
